/**
 * @fileoverview Plugin for handling paste operations of Aha! Editor fragments.
 * Processes clipboard data with the Aha! Editor format and logs paste operations to Sentry.
 */

import { type EditorPlugin } from '../../types/aha-editor';
import { Editor } from 'slate';
import { decodeFragment } from '../../util/fragment';
import { removeMediaAncestors } from './removeMediaAncestors';
import AhaEditor from '../../AhaEditor';
import TableUtils from '../TablePlugin/TableUtils';
import { AddScrubbedSentryBreadcrumb } from '../../SentryLogger';

/**
 * Editor plugin for handling paste operations with Aha! Editor fragment format.
 * Decodes and processes clipboard data, transforms fragments, and logs operations.
 */
const PasteFragmentPlugin: EditorPlugin = {
  name: 'Paste fragment plugin',
  /**
   * Handles insertion of Aha! Editor fragment data from clipboard.
   * Processes the fragment, transforms it if needed, and inserts into editor.
   * @param editor - The Slate editor instance
   * @param data - DataTransfer object containing clipboard data
   * @returns true if handled, false if not applicable
   */
  insertData: (editor: Editor, data: DataTransfer) => {
    if (!data.types.includes(`application/${AhaEditor.clipboardFormatKey}`)) {
      return false;
    }

    const ahaEditor = AhaEditor.getForEditor(editor);
    const fragmentString = data.getData(
      `application/${AhaEditor.clipboardFormatKey}`
    );

    const decodedFragment = decodeFragment(fragmentString);
    const fragment = ahaEditor
      ? ahaEditor.transformPastedFragment(decodedFragment, data)
      : decodedFragment;

    const blockChildren = TableUtils.extractBlocksFromSingleCell(fragment);
    AddScrubbedSentryBreadcrumb({
      message: 'Paste fragment from HTML',
      data: blockChildren,
    });

    if (removeMediaAncestors(editor, blockChildren)) {
      return true;
    }

    Editor.insertFragment(editor, blockChildren);
    return true;
  },
};

export default PasteFragmentPlugin;
